import { useState, useCallback } from 'react';
import { validateField, validateForm } from './formValidation';

/**
 * Custom hook for form validation
 * @param {string} formName - The name of the form (e.g., 'backPay', 'bonus', etc.)
 * @returns {object} - Validation state and functions
 */
export const useFormValidation = (formName) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  /**
   * Validate a single field
   */
  const validateSingleField = useCallback(
    (fieldName, value, formData = null) => {
      const error = validateField(formName, fieldName, value, formData);

      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[fieldName] = error;
        } else {
          delete newErrors[fieldName];
        }
        return newErrors;
      });

      return error;
    },
    [formName]
  );

  /**
   * Validate entire form
   */
  const validateEntireForm = useCallback(
    (formData) => {
      const { isValid, errors } = validateForm(formName, formData);
      setValidationErrors(errors);
      return isValid;
    },
    [formName]
  );

  /**
   * Mark field as touched (for blur events)
   */
  const markFieldTouched = useCallback((fieldName) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
  }, []);

  /**
   * Check if field has been touched
   */
  const isFieldTouched = useCallback(
    (fieldName) => {
      return touchedFields[fieldName] || false;
    },
    [touchedFields]
  );

  /**
   * Get error for a specific field (ignores touched state - use for displaying errors after submission)
   */
  const getFieldError = useCallback(
    (fieldName) => {
      return validationErrors[fieldName] || null;
    },
    [validationErrors]
  );

  /**
   * Get error for a specific field (only if touched - use for real-time validation during editing)
   */
  const getFieldErrorIfTouched = useCallback(
    (fieldName) => {
      if (!touchedFields[fieldName]) {
        return null;
      }
      return validationErrors[fieldName] || null;
    },
    [touchedFields, validationErrors]
  );

  /**
   * Check if field should show error
   */
  const shouldShowError = useCallback(
    (fieldName) => {
      return touchedFields[fieldName] && !!validationErrors[fieldName];
    },
    [touchedFields, validationErrors]
  );

  /**
   * Reset validation state
   */
  const resetValidation = useCallback(() => {
    setValidationErrors({});
    setTouchedFields({});
  }, []);

  /**
   * Clear error for specific field
   */
  const clearFieldError = useCallback((fieldName) => {
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  /**
   * Handle field blur - validate if field is filled
   */
  const handleFieldBlur = useCallback(
    (fieldName, value, formData = null) => {
      markFieldTouched(fieldName);
      if (value !== null && value !== undefined && value !== '') {
        validateSingleField(fieldName, value, formData);
      }
    },
    [markFieldTouched, validateSingleField]
  );

  /**
   * Handle field change - validate immediately if already touched
   */
  const handleFieldChange = useCallback(
    (fieldName, value, formData = null) => {
      if (touchedFields[fieldName]) {
        validateSingleField(fieldName, value, formData);
      }
    },
    [touchedFields, validateSingleField]
  );

  /**
   * Get error for array field
   * @param {string} arrayPath - e.g., 'bonuses'
   * @param {number} index - array index
   * @param {string} fieldName - e.g., 'date'
   */
  const getArrayFieldError = useCallback(
    (arrayPath, index, fieldName) => {
      const errorKey = `${arrayPath}[${index}].${fieldName}`;
      return validationErrors[errorKey] || null;
    },
    [validationErrors]
  );

  /**
   * Check if array field has error
   */
  const hasArrayFieldError = useCallback(
    (arrayPath, index, fieldName) => {
      const errorKey = `${arrayPath}[${index}].${fieldName}`;
      return !!validationErrors[errorKey];
    },
    [validationErrors]
  );

  /**
   * Get all errors for a specific array index
   */
  const getArrayItemErrors = useCallback(
    (arrayPath, index) => {
      const prefix = `${arrayPath}[${index}].`;
      const itemErrors = {};

      Object.keys(validationErrors).forEach((key) => {
        if (key.startsWith(prefix)) {
          const fieldName = key.substring(prefix.length);
          itemErrors[fieldName] = validationErrors[key];
        }
      });

      return itemErrors;
    },
    [validationErrors]
  );

  /**
   * Check if any field in array item has error
   */
  const hasArrayItemErrors = useCallback(
    (arrayPath, index) => {
      const prefix = `${arrayPath}[${index}].`;
      return Object.keys(validationErrors).some((key) =>
        key.startsWith(prefix)
      );
    },
    [validationErrors]
  );

  /**
   * Clear all errors for a specific array item
   */
  const clearArrayItemErrors = useCallback((arrayPath, index) => {
    const prefix = `${arrayPath}[${index}].`;
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith(prefix)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  }, []);

  return {
    validationErrors,
    touchedFields,
    validateSingleField,
    validateEntireForm,
    markFieldTouched,
    isFieldTouched,
    getFieldError, // Always returns error if present (for submission mode)
    getFieldErrorIfTouched, // Only returns error if field was touched (for real-time validation)
    shouldShowError,
    resetValidation,
    clearFieldError,
    handleFieldBlur,
    handleFieldChange,
    hasErrors: Object.keys(validationErrors).length > 0,
    errorCount: Object.keys(validationErrors).length,
    // Array-specific methods
    getArrayFieldError,
    hasArrayFieldError,
    getArrayItemErrors,
    hasArrayItemErrors,
    clearArrayItemErrors,
  };
};

/**
 * Get nested value from object using dot notation
 */
export const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Set nested value in object using dot notation
 */
export const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  const target = keys.reduce((current, key) => {
    if (!current[key]) current[key] = {};
    return current[key];
  }, obj);
  target[lastKey] = value;
  return obj;
};

/**
 * Helper to add validation CSS class to input
 */
export const getValidationClass = (baseClass, fieldName, shouldShowError) => {
  return shouldShowError(fieldName) ? `${baseClass} error` : baseClass;
};
